/**
 * Single source of truth for feature flags with typed defaults
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type FeatureFlag = 
  // Public features
  | 'enable_templates' 
  | 'enable_assistants_api' 
  | 'assistant_only_mode'
  // Essential admin features (always enabled)
  | 'enable_analytics'
  | 'enable_dashboard'
  | 'enable_users'
  | 'enable_ai_settings'
  | 'enable_rag_data'
  | 'enable_payments'
  // Advanced admin features (Phase 7+)
  | 'enable_security_scan'
  | 'enable_audit_logs'
  | 'enable_system_logs'
  | 'enable_qa_dashboard'
  | 'enable_ai_monitoring'
  | 'enable_system_health'
  | 'enable_secrets_audit'
  | 'enable_data_engine'
  | 'enable_rag_quality'
  | 'enable_credit_pricing'
  // Trust Platform
  | 'enable_trust_platform'
  // Development tools
  | 'enable_seed_tools'
  | 'enable_test_fixtures';

const DEFAULTS: Record<FeatureFlag, boolean> = {
  // Public
  enable_templates: true,
  enable_assistants_api: true,
  assistant_only_mode: false,
  // Essential (always enabled)
  enable_analytics: true,
  enable_dashboard: true,
  enable_users: true,
  enable_ai_settings: true,
  enable_rag_data: true,
  enable_payments: true,
  // Advanced (deferred to Phase 7+)
  enable_security_scan: false,
  enable_audit_logs: false,
  enable_system_logs: false,
  enable_qa_dashboard: false,
  enable_ai_monitoring: false,
  enable_system_health: false,
  enable_secrets_audit: false,
  enable_data_engine: false,
  enable_rag_quality: false,
  enable_credit_pricing: false,
  // Trust Platform
  enable_trust_platform: true,
  // Development tools (only in dev mode)
  enable_seed_tools: import.meta.env.DEV,
  enable_test_fixtures: import.meta.env.DEV,
};

/**
 * Single query hook for all feature flags
 * Merges admin_settings with typed defaults
 */
interface PublicSettingsResponse {
  free_trial_credits: number;
  guest_free_messages: number;
  low_balance_threshold: number;
  ai_identity_name: string;
  feature_flags: {
    features: Record<string, boolean>;
  };
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags-unified'],
    queryFn: async () => {
      // Use secure RPC function for public settings
      const { data, error } = await supabase.rpc('get_public_settings');
      
      if (error) {
        console.error('Failed to fetch feature flags:', error);
        return DEFAULTS;
      }
      
      const typedData = data as unknown as PublicSettingsResponse;
      const featureFlags = typedData?.feature_flags as any || {};
      const publicFlags = featureFlags?.features || {};
      const adminFlags = featureFlags?.admin || {};
      
      // Merge all flags: defaults < public < admin (admin overrides)
      return { ...DEFAULTS, ...publicFlags, ...adminFlags };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Getter with default fallback
 */
export function getFlag(flag: FeatureFlag, flags: any): boolean {
  return flags?.[flag] ?? DEFAULTS[flag];
}

/**
 * Hook to check if a specific feature is enabled
 * Convenience wrapper around useFeatureFlags
 */
export function useIsFeatureEnabled(feature: FeatureFlag): boolean {
  const { data: flags } = useFeatureFlags();
  return getFlag(feature, flags);
}
